!function() {
  "use strict";

  const selectors = {
    state: "[data-hamster-state]",
    hitbox: "[data-hamster-hitbox]",
    pointer: "[data-meter-pointer]"
  };
  const classes = {
    active: "is-active"
  };

  /** 0 = min distance (NEEDY / left), 1 = max (OVERSTIM / right). */
  var METER_ANGLE_MIN = -90;
  var METER_ANGLE_MAX = 90;

  function meterAngleFromProgress(t) {
    t = Math.max(0, Math.min(1, t));
    return t * (METER_ANGLE_MAX - METER_ANGLE_MIN) + METER_ANGLE_MIN;
  }

  customElements.get("hamster-petter") || customElements.define("hamster-petter", class extends HTMLElement {
    constructor() {
      super();
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleTouchCancel = this.handleTouchCancel.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.decayStep = this.decayStep.bind(this);
    }

    connectedCallback() {
      this.states = this.querySelectorAll(selectors.state);
      if (this.states.length < 2) return;

      this.hitbox = this.querySelector(selectors.hitbox);
      this.pointer = this.querySelector(selectors.pointer);
      if (!this.hitbox) return;

      /* 0–100% in theme editor: 0% = most stroking, 100% = meter fills quickly (maps to internal 1–100 scale). */
      var p = parseFloat(this.dataset.pettingSensitivity);
      if (isNaN(p)) p = 50;
      p = Math.max(0, Math.min(100, p));
      var sensEquiv = 1 + (p / 100) * 99;
      this.budget = (400 * 100) / sensEquiv;
      var mobileHamster =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(max-width: 749px)").matches;
      this.hamsterMobileViewport = !!mobileHamster;
      if (mobileHamster) {
        this.budget *= 2;
      }
      var decayPct = parseFloat(this.dataset.decaySpeed) || 50;
      this.decayRate = 10 * Math.max(1, decayPct);
      this.distance = 0;
      this.lastX = 0;
      this.lastY = 0;
      this.activeIndex = 0;
      this.decayRAF = null;
      this.lastDecayTime = 0;
      this.idleTimeout = null;

      this.hitbox.addEventListener("mouseenter", this.handleMouseEnter);
      this.hitbox.addEventListener("mousemove", this.handleMouseMove);
      this.hitbox.addEventListener("mouseleave", this.handleMouseLeave);
      this.hitbox.addEventListener("touchstart", this.handleTouchStart, {passive: true});
      this.hitbox.addEventListener("touchmove", this.handleTouchMove, {passive: true});
      this.hitbox.addEventListener("touchend", this.handleTouchEnd, {passive: true});
      this.hitbox.addEventListener("touchcancel", this.handleTouchCancel, {passive: true});
      this.hitbox.addEventListener("pointerdown", this.handlePointerDown, {passive: true});
    }

    disconnectedCallback() {
      if (this.hitbox) {
        this.hitbox.removeEventListener("mouseenter", this.handleMouseEnter);
        this.hitbox.removeEventListener("mousemove", this.handleMouseMove);
        this.hitbox.removeEventListener("mouseleave", this.handleMouseLeave);
        this.hitbox.removeEventListener("touchstart", this.handleTouchStart);
        this.hitbox.removeEventListener("touchmove", this.handleTouchMove);
        this.hitbox.removeEventListener("touchend", this.handleTouchEnd);
        this.hitbox.removeEventListener("touchcancel", this.handleTouchCancel);
        this.hitbox.removeEventListener("pointerdown", this.handlePointerDown);
      }
      this.clearIdleTimeout();
      this.cancelDecay();
    }

    cancelDecay() {
      if (this.decayRAF != null) {
        cancelAnimationFrame(this.decayRAF);
        this.decayRAF = null;
      }
    }

    clearIdleTimeout() {
      if (this.idleTimeout != null) {
        clearTimeout(this.idleTimeout);
        this.idleTimeout = null;
      }
    }

    initTracking(clientX, clientY) {
      this.lastX = clientX;
      this.lastY = clientY;
      this.clearIdleTimeout();
      this.idleTimeout = setTimeout(() => this.beginDecay(), 200);
    }

    accumulateDistance(clientX, clientY) {
      this.clearIdleTimeout();
      this.cancelDecay();
      const dx = clientX - this.lastX;
      const dy = clientY - this.lastY;
      this.lastX = clientX;
      this.lastY = clientY;
      this.distance = Math.min(this.distance + Math.hypot(dx, dy), this.budget);
      this.updateState();
      this.idleTimeout = setTimeout(() => this.beginDecay(), 200);
    }

    /**
     * Click/tap without movement adds no distance via accumulateDistance; bump slightly like a small hover stroke.
     */
    nudgeFromPointerPress(clientX, clientY) {
      if (this.budget <= 0) return;
      this.clearIdleTimeout();
      this.cancelDecay();
      if (typeof clientX === "number" && typeof clientY === "number") {
        this.lastX = clientX;
        this.lastY = clientY;
      }
      /* ~11% of budget per press; cap avoids one-tap max on huge budgets */
      var bump = Math.min(this.budget * 0.11, Math.hypot(96, 96));
      if (this.hamsterMobileViewport) bump *= 2;
      this.distance = Math.min(this.distance + bump, this.budget);
      this.updateState();
      this.idleTimeout = setTimeout(() => this.beginDecay(), 200);
    }

    handlePointerDown(e) {
      if (e.button !== 0) return;
      this.nudgeFromPointerPress(e.clientX, e.clientY);
    }

    updateState() {
      if (this.pointer && this.budget > 0) {
        var deg = meterAngleFromProgress(this.distance / this.budget);
        this.pointer.style.transform = "rotate(" + deg + "deg)";
      }

      const n = this.states.length;
      if (n <= 1) return;
      var newIndex = 0;
      if (this.distance > 0 && this.budget > 0) {
        var d = this.distance * n * (n - 1) / this.budget;
        newIndex = Math.min(Math.floor((Math.sqrt(1 + 4 * d) - 1) / 2), n - 1);
      }
      if (newIndex === this.activeIndex) return;
      var prevIndex = this.activeIndex;
      this.states[this.activeIndex].classList.remove(classes.active);
      this.states[newIndex].classList.add(classes.active);
      this.activeIndex = newIndex;
      this.dispatchEvent(
        new CustomEvent("hamster:active-state-changed", {
          bubbles: true,
          detail: { activeIndex: newIndex, previousIndex: prevIndex }
        })
      );
    }

    decayStep(timestamp) {
      if (this.lastDecayTime === 0) this.lastDecayTime = timestamp;
      const deltaTime = (timestamp - this.lastDecayTime) / 1000;
      this.lastDecayTime = timestamp;

      this.distance = Math.max(0, this.distance - this.decayRate * deltaTime);
      this.updateState();

      if (this.distance > 0) {
        this.decayRAF = requestAnimationFrame(this.decayStep);
      } else {
        this.decayRAF = null;
        this.lastDecayTime = 0;
      }
    }

    beginDecay() {
      this.cancelDecay();
      this.lastDecayTime = 0;
      this.decayRAF = requestAnimationFrame(this.decayStep);
    }

    handleMouseEnter(e) {
      this.initTracking(e.clientX, e.clientY);
    }

    handleMouseMove(e) {
      this.accumulateDistance(e.clientX, e.clientY);
    }

    handleMouseLeave() {
      this.clearIdleTimeout();
      this.beginDecay();
    }

    handleTouchStart(e) {
      if (e.touches.length === 0) return;
      this.initTracking(e.touches[0].clientX, e.touches[0].clientY);
    }

    handleTouchMove(e) {
      if (e.touches.length === 0) return;
      this.accumulateDistance(e.touches[0].clientX, e.touches[0].clientY);
    }

    handleTouchEnd() {
      this.clearIdleTimeout();
      this.beginDecay();
    }

    handleTouchCancel() {
      this.clearIdleTimeout();
      this.beginDecay();
    }
  });

  /**
   * Chromium ignores SVG in cursor:url(). Rasterize to a PNG data URL and set --hamster-custom-cursor on the section.
   */
  function initHamsterSvgRasterCursor() {
    var el = document.querySelector("[data-hamster-raster-cursor]");
    if (!el) return;
    var src = el.getAttribute("data-cursor-src");
    if (!src) return;
    var section = el.closest("[id^='shopify-section']");
    if (!section) return;
    var hx = parseFloat(el.getAttribute("data-cursor-hx") || "16");
    var hy = parseFloat(el.getAttribute("data-cursor-hy") || "16");
    if (isNaN(hx)) hx = 16;
    if (isNaN(hy)) hy = 16;

    var img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = function() {
      var w = img.naturalWidth;
      var h = img.naturalHeight;
      if (w < 1 || h < 1) return;
      var max = 128;
      var scale = Math.min(max / w, max / h, 1);
      var cw = Math.max(1, Math.round(w * scale));
      var ch = Math.max(1, Math.round(h * scale));
      var canvas = document.createElement("canvas");
      canvas.width = cw;
      canvas.height = ch;
      var ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, cw, ch);
      var dataUrl;
      try {
        dataUrl = canvas.toDataURL("image/png");
      } catch (e) {
        return;
      }
      var hxScaled = Math.round(hx * (cw / w));
      var hyScaled = Math.round(hy * (ch / h));
      hxScaled = Math.max(0, Math.min(cw - 1, hxScaled));
      hyScaled = Math.max(0, Math.min(ch - 1, hyScaled));
      section.style.setProperty(
        "--hamster-custom-cursor",
        "url(" + JSON.stringify(dataUrl) + ") " + hxScaled + " " + hyScaled + ", pointer"
      );
    };
    img.onerror = function() {};
    img.src = src;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initHamsterSvgRasterCursor);
  } else {
    initHamsterSvgRasterCursor();
  }

  /**
   * Overlay: optional delayed reveal (skip if user touches hitbox first); hide after 3rd mood (activeIndex >= 2).
   */
  function initHamsterOverlayBehavior() {
    document.querySelectorAll("[data-hamster-overlay-root]").forEach(function(root) {
      if (root.getAttribute("data-hamster-overlay-bound") === "true") return;
      var petter = root.closest("hamster-petter");
      if (!petter) return;
      root.setAttribute("data-hamster-overlay-bound", "true");

      petter.addEventListener("hamster:active-state-changed", function(ev) {
        var d = ev.detail;
        if (!d || typeof d.activeIndex !== "number") return;
        if (d.activeIndex >= 2) {
          root.classList.add("hamster-landing__hamster-overlay--dismissed");
        }
      });

      var delayMs = parseInt(root.getAttribute("data-overlay-delay-ms") || "0", 10);
      if (isNaN(delayMs)) delayMs = 0;

      var hitbox = petter.querySelector("[data-hamster-hitbox]");
      var revealTimer = null;

      function cancelDelayedReveal() {
        if (!root.classList.contains("hamster-landing__hamster-overlay--delay-pending")) return;
        if (revealTimer != null) {
          clearTimeout(revealTimer);
          revealTimer = null;
        }
        root.classList.remove("hamster-landing__hamster-overlay--delay-pending");
        root.classList.add("hamster-landing__hamster-overlay--delay-cancelled");
      }

      function scheduleReveal() {
        if (delayMs <= 0) return;
        revealTimer = setTimeout(function() {
          revealTimer = null;
          if (root.classList.contains("hamster-landing__hamster-overlay--delay-cancelled")) return;
          root.classList.remove("hamster-landing__hamster-overlay--delay-pending");
        }, delayMs);
      }

      if (root.classList.contains("hamster-landing__hamster-overlay--delay-pending")) {
        if (hitbox) {
          hitbox.addEventListener("pointerdown", cancelDelayedReveal, { passive: true });
          hitbox.addEventListener("touchstart", cancelDelayedReveal, { passive: true });
        }
        scheduleReveal();
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initHamsterOverlayBehavior);
  } else {
    initHamsterOverlayBehavior();
  }
  document.addEventListener("shopify:section:load", initHamsterOverlayBehavior);

  /** Homepage gate: full-screen hamster until CTA; localStorage remembers dismissal. */
  var HAMSTER_HOME_GATE_SUPPRESS_SESSION_KEY = "hamsterSuppressHomeGateOnce";

  var hamsterGate = {
    cfg: null,
    sectionEl: null,
    clickHandler: null,
    timeoutFallback: null
  };

  function hamsterHomeGateApplyPreskippedState(el) {
    document.documentElement.classList.remove("hamster-home-gate-pending");
    resetHamsterHomeGateListeners();
    el.classList.add("hamster-home-gate--dismissed");
    el.classList.add("is-hamster-home-gate-section");
    hamsterGate.cfg = null;
    hamsterGate.sectionEl = null;
  }

  function resetHamsterHomeGateListeners() {
    if (hamsterGate.clickHandler) {
      document.body.removeEventListener("click", hamsterGate.clickHandler, true);
      hamsterGate.clickHandler = null;
    }
    if (hamsterGate.timeoutFallback != null) {
      clearTimeout(hamsterGate.timeoutFallback);
      hamsterGate.timeoutFallback = null;
    }
    document.body.classList.remove("hamster-home-gate-active");
  }

  function teardownHamsterHomeGateFully() {
    resetHamsterHomeGateListeners();
    document.documentElement.classList.remove("hamster-home-gate-pending");
  }

  /**
   * Header (announcement ticker, etc.) is display:none while the gate or asset splash is active.
   * ticker-bar measures width on upgrade; dispatch after layout is real so marquees re-init.
   */
  function dispatchThemeResizeWidthDeferred() {
    function fire() {
      try {
        document.dispatchEvent(new CustomEvent("theme:resize:width", { bubbles: true }));
      } catch (eResize) {}
    }
    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(function() {
        requestAnimationFrame(fire);
      });
    } else {
      fire();
    }
    window.setTimeout(fire, 120);
    window.setTimeout(fire, 450);
  }

  /**
   * Featured collection + brand manifesto init while the gate is active (zero-size / wrong scroll).
   * Re-run after reveal: carousel recentres on slide 2 (mobile); manifesto icons re-place.
   */
  function reinitIndexSectionsAfterHamsterGateReveal() {
    function run() {
      try {
        dispatchThemeResizeWidthDeferred();
        if (typeof window.initFeaturedCollectionCarousel === "function") {
          document.querySelectorAll('[id^="FeaturedCollection--"]').forEach(function (sectionEl) {
            window.initFeaturedCollectionCarousel(sectionEl);
          });
        }
        if (typeof window.refreshBrandManifestoIcons === "function") {
          window.refreshBrandManifestoIcons();
        }
      } catch (eReinit) {}
    }
    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(function() {
        requestAnimationFrame(run);
      });
    } else {
      run();
    }
    window.setTimeout(run, 120);
    window.setTimeout(run, 450);
  }

  function hamsterHomeGateClickHandler(ev) {
    if (!document.body.classList.contains("hamster-home-gate-active")) return;
    var anchor = ev.target.closest && ev.target.closest("[data-hamster-home-gate-cta]");
    if (!anchor) return;
    ev.preventDefault();
    ev.stopPropagation();
    var el = hamsterGate.sectionEl;
    if (!el || el.classList.contains("hamster-home-gate--exiting")) return;

    /* Persist before exit animation so shopify:section:load / DOM swap cannot reopen the gate first. */
    try {
      if (
        hamsterGate.cfg &&
        hamsterGate.cfg.storageKey &&
        hamsterGate.cfg.rememberDismissal !== false
      ) {
        window.localStorage.setItem(hamsterGate.cfg.storageKey, "1");
      }
    } catch (ePersist) {}

    function finishDismiss() {
      if (hamsterGate.timeoutFallback != null) {
        clearTimeout(hamsterGate.timeoutFallback);
        hamsterGate.timeoutFallback = null;
      }
      el.classList.remove("hamster-home-gate--exiting");
      el.classList.add("hamster-home-gate--dismissed");
      document.body.classList.remove("hamster-home-gate-active");
      document.documentElement.classList.remove("hamster-home-gate-pending");
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      window.scrollTo(0, 0);
      if (hamsterGate.clickHandler) {
        document.body.removeEventListener("click", hamsterGate.clickHandler, true);
        hamsterGate.clickHandler = null;
      }
      var main = document.getElementById("MainContent");
      if (main) {
        main.setAttribute("tabindex", "-1");
        try {
          main.focus({ preventScroll: true });
        } catch (e2) {}
        main.removeAttribute("tabindex");
      }
      hamsterGate.cfg = null;
      hamsterGate.sectionEl = null;
      reinitIndexSectionsAfterHamsterGateReveal();
    }

    el.classList.add("hamster-home-gate--exiting");
    var reduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      finishDismiss();
      return;
    }

    function onEnd(ev2) {
      if (ev2.target !== el) return;
      if (ev2.propertyName !== "opacity" && ev2.propertyName !== "transform") return;
      el.removeEventListener("transitionend", onEnd);
      finishDismiss();
    }
    el.addEventListener("transitionend", onEnd);
    hamsterGate.timeoutFallback = window.setTimeout(function() {
      el.removeEventListener("transitionend", onEnd);
      finishDismiss();
    }, 550);
  }

  function initHamsterHomeGate() {
    resetHamsterHomeGateListeners();

    if (!document.body.classList.contains("template-index")) {
      document.documentElement.classList.remove("hamster-home-gate-pending");
      return;
    }

    var script = document.querySelector(
      'script[type="application/json"][id^="hamster-home-gate-config-"]'
    );
    if (!script) {
      document.documentElement.classList.remove("hamster-home-gate-pending");
      return;
    }

    var cfg;
    try {
      cfg = JSON.parse(script.textContent || "{}");
    } catch (e) {
      document.documentElement.classList.remove("hamster-home-gate-pending");
      return;
    }
    if (!cfg.sectionId || !cfg.storageKey) {
      document.documentElement.classList.remove("hamster-home-gate-pending");
      return;
    }

    var el = document.getElementById("shopify-section-" + cfg.sectionId);
    if (!el) {
      document.documentElement.classList.remove("hamster-home-gate-pending");
      return;
    }

    try {
      if (window.sessionStorage.getItem(HAMSTER_HOME_GATE_SUPPRESS_SESSION_KEY) === "1") {
        window.sessionStorage.removeItem(HAMSTER_HOME_GATE_SUPPRESS_SESSION_KEY);
        hamsterHomeGateApplyPreskippedState(el);
        return;
      }
    } catch (eSuppress) {}

    hamsterGate.cfg = cfg;
    hamsterGate.sectionEl = el;

    try {
      var rememberDismissal = cfg.rememberDismissal !== false;
      if (rememberDismissal && window.localStorage.getItem(cfg.storageKey) === "1") {
        hamsterHomeGateApplyPreskippedState(el);
        return;
      }
    } catch (e2) {
      document.documentElement.classList.remove("hamster-home-gate-pending");
      hamsterGate.cfg = null;
      hamsterGate.sectionEl = null;
      return;
    }

    el.classList.add("is-hamster-home-gate-section");
    document.documentElement.classList.remove("hamster-home-gate-pending");
    document.body.classList.add("hamster-home-gate-active");
    window.scrollTo(0, 0);

    hamsterGate.clickHandler = hamsterHomeGateClickHandler;
    document.body.addEventListener("click", hamsterGate.clickHandler, true);
  }

  function initHamsterHomeGateOnSectionLoad(ev) {
    if (!ev || !ev.target || !ev.target.id) return;
    var script = document.querySelector(
      'script[type="application/json"][id^="hamster-home-gate-config-"]'
    );
    if (!script) return;
    var cfg0;
    try {
      cfg0 = JSON.parse(script.textContent || "{}");
    } catch (e0) {
      return;
    }
    if (!cfg0.sectionId) return;
    if (ev.target.id !== "shopify-section-" + cfg0.sectionId) return;
    initHamsterHomeGate();
  }

  function initHamsterSplashHomeGateSuppress() {
    var wrap = document.querySelector("[data-hamster-splash-cta]");
    if (!wrap) return;
    var rootUrl = wrap.getAttribute("data-hamster-root-url");
    if (!rootUrl) return;
    wrap.addEventListener("click", function(ev) {
      var a = ev.target.closest && ev.target.closest("a[href]");
      if (!a || !wrap.contains(a)) return;
      try {
        var dest = new URL(a.getAttribute("href"), window.location.origin);
        var home = new URL(rootUrl, window.location.origin);
        if (dest.hostname !== home.hostname) return;
        var dp = dest.pathname.replace(/\/+$/, "") || "/";
        var hp = home.pathname.replace(/\/+$/, "") || "/";
        if (dp === hp) {
          window.sessionStorage.setItem(HAMSTER_HOME_GATE_SUPPRESS_SESSION_KEY, "1");
        }
      } catch (eSplash) {}
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initHamsterSplashHomeGateSuppress);
  } else {
    initHamsterSplashHomeGateSuppress();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initHamsterHomeGate);
  } else {
    initHamsterHomeGate();
  }
  document.addEventListener("shopify:section:load", initHamsterHomeGateOnSectionLoad);

  document.addEventListener("shopify:section:unload", function(ev) {
    if (!hamsterGate.sectionEl || !ev.target || ev.target !== hamsterGate.sectionEl) return;
    teardownHamsterHomeGateFully();
    hamsterGate.sectionEl = null;
    hamsterGate.cfg = null;
  });

  /**
   * Optional splash: hide main content until every tracked img has loaded (bg uses same URL via preload img).
   */
  function imgElementLoadPromise(img) {
    return new Promise(function(resolve) {
      if (img.complete && img.naturalWidth > 0) {
        resolve();
        return;
      }
      function done() {
        img.removeEventListener("load", onLoad);
        img.removeEventListener("error", onError);
        resolve();
      }
      function onLoad() {
        done();
      }
      function onError() {
        done();
      }
      img.addEventListener("load", onLoad);
      img.addEventListener("error", onError);
    });
  }

  var HAMSTER_ASSET_MIN_MS = 1000;

  function finishAssetLoading(root) {
    root.classList.remove("hamster-landing__root--asset-loading");
    root.classList.add("hamster-landing__root--assets-ready");
    var secId = root.getAttribute("data-shopify-section-id");
    if (secId) {
      document.documentElement.classList.remove("hamster-asset-skip-" + secId);
    }
    dispatchThemeResizeWidthDeferred();
  }

  function nowMs() {
    return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
  }

  function initHamsterAssetLoading() {
    document.querySelectorAll(".hamster-landing__root--asset-loading").forEach(function(root) {
      if (root.getAttribute("data-hamster-assets-bound") === "true") return;

      var remember = root.getAttribute("data-hamster-remember-shell") === "true";
      var storageKey = root.getAttribute("data-hamster-asset-storage-key");

      if (remember && storageKey) {
        try {
          if (window.localStorage.getItem(storageKey) === "1") {
            root.setAttribute("data-hamster-assets-bound", "true");
            finishAssetLoading(root);
            return;
          }
        } catch (eSkip) {}
      }

      root.setAttribute("data-hamster-assets-bound", "true");

      var imgs = root.querySelectorAll("img.hamster-landing__tracked-img");

      var tasks = [];
      imgs.forEach(function(img) {
        tasks.push(imgElementLoadPromise(img));
      });

      var maxMs = 18000;
      var timeoutP = new Promise(function(resolve) {
        setTimeout(resolve, maxMs);
      });

      var t0 = nowMs();

      Promise.race([Promise.all(tasks), timeoutP]).then(function() {
        function reveal() {
          requestAnimationFrame(function() {
            finishAssetLoading(root);
            if (remember && storageKey) {
              try {
                window.localStorage.setItem(storageKey, "1");
              } catch (eStore) {}
            }
          });
        }
        var elapsed = nowMs() - t0;
        var remaining = HAMSTER_ASSET_MIN_MS - elapsed;
        if (remaining > 0) {
          setTimeout(reveal, remaining);
        } else {
          reveal();
        }
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initHamsterAssetLoading);
  } else {
    initHamsterAssetLoading();
  }
  document.addEventListener("shopify:section:load", initHamsterAssetLoading);

  /** In-app / mail WebViews: clamp section height to real visible viewport (pairs with --hamster-visible-height in section CSS). */
  var hamsterViewportListenersAttached = false;

  function setHamsterVisibleHeightVar() {
    if (!document.querySelector(".hamster-landing")) return;
    var h = window.innerHeight;
    if (window.visualViewport && typeof window.visualViewport.height === "number") {
      h = window.visualViewport.height;
    }
    document.documentElement.style.setProperty("--hamster-visible-height", h + "px");
  }

  function initHamsterVisibleHeight() {
    if (!document.querySelector(".hamster-landing")) return;
    if (!hamsterViewportListenersAttached) {
      hamsterViewportListenersAttached = true;
      window.addEventListener("resize", setHamsterVisibleHeightVar);
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", setHamsterVisibleHeightVar);
        window.visualViewport.addEventListener("scroll", setHamsterVisibleHeightVar);
      }
    }
    setHamsterVisibleHeightVar();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initHamsterVisibleHeight);
  } else {
    initHamsterVisibleHeight();
  }
  document.addEventListener("shopify:section:load", initHamsterVisibleHeight);
}();
